import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import { hasExplorerOnboarding } from "../explorer/hasExplorerOnboarding";

/**
 * Explorer users without full membership: show sidebar shell but lock nav behind membership.
 * Paid or cancelled members use the normal sidebar behaviour.
 * `/register-user` (Personal Info → Confirmation) stays full-width with no sidebar.
 */
export function useMembershipSidebarGate() {
  const { user, objectId } = useAppContext();
  const location = useLocation();
  const authed = isLoggedIn();

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: authed && !!objectId,
    retry: false,
    staleTime: 60_000,
  });

  const isExplorer = useMemo(() => hasExplorerOnboarding(explorerMe), [explorerMe]);

  const isCancelled = useMemo(() => {
    if (user?.user?.status === "InActive") return true;
    const st = user?.user?.stripeStatus?.toLowerCase?.();
    return st === "canceled" || st === "cancelled";
  }, [user?.user?.status, user?.user?.stripeStatus]);

  const hasPaid = user?.user?.hasPaid === 1;
  const isRegistrationWizard = location.pathname === "/register-user";

  /** Entire membership wizard including Confirmation after payment. */
  const hideSidebarOnRegistrationWizard = isRegistrationWizard;

  const lockSidebarForMembership = useMemo(
    () =>
      authed &&
      !!user?.user &&
      !hasPaid &&
      !isCancelled &&
      isExplorer &&
      !hideSidebarOnRegistrationWizard,
    [
      authed,
      user?.user,
      hasPaid,
      isCancelled,
      isExplorer,
      hideSidebarOnRegistrationWizard,
    ],
  );

  const treatAsSidebarMember = hideSidebarOnRegistrationWizard
    ? false
    : hasPaid || isCancelled || lockSidebarForMembership;

  return {
    lockSidebarForMembership,
    treatAsSidebarMember,
    hideSidebarOnRegistrationWizard,
  };
}
